import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import PageHero from "@/components/layout/PageHero";
import SEO from "@/components/layout/SEO";
import FinalCTA from "@/components/home/FinalCTA";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock, FileText } from "lucide-react";

export default function Insights() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");

  useEffect(() => {
    setLoading(true);
    const params = category === "All" ? {} : { category };
    http
      .get("/insights", { params })
      .then((r) => setPosts(r.data || []))
      .finally(() => setLoading(false));
  }, [category]);

  const categories = Array.from(new Set(["All", ...posts.map((p) => p.category)]));

  return (
    <div data-testid="page-insights">
      <PageHero
        overline="Insights"
        title={<>Answers to the questions international students <span className="italic text-[#D7141A]">actually</span> ask.</>}
        subtitle="Short, specific articles written from fifteen years of work inside Polish higher education. No filler, no affiliate noise — just useful."
      />

      <section className="pb-24">
        <div className="container-sp">
          <div className="flex flex-wrap gap-2 mb-10" data-testid="insights-filters">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`insights-filter-${c}`}
                className={`px-4 py-2 rounded-full text-xs transition-all ${
                  category === c
                    ? "bg-[#0D1B2A] text-white"
                    : "bg-white text-[#0D1B2A] border border-[#EFEEE8] hover:border-[#D7141A]/40"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {loading && <div className="text-[#718096]">Loading…</div>}
          {!loading && posts.length === 0 && (
            <div className="card-sp text-center py-20" data-testid="insights-empty">
              <FileText className="w-10 h-10 text-[#D7141A] mx-auto mb-4" />
              <h3 className="font-serif text-2xl text-[#0D1B2A]">Articles coming soon.</h3>
              <p className="text-[#4A5568] mt-3 max-w-md mx-auto">
                We're preparing the first set of practical articles on admissions, visa
                routes, and university selection. Check back shortly — or subscribe
                below and we'll send you the first drop.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {!loading &&
              posts.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                >
                  <Link
                    to={`/insights/${p.slug}`}
                    className="group block card-sp h-full p-0 overflow-hidden"
                    data-testid={`insights-card-${p.slug}`}
                  >
                    {p.cover_image && (
                      <div className="h-44 overflow-hidden">
                        <img
                          src={p.cover_image}
                          alt={p.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                    )}
                    <div className="p-7">
                      <div className="overline">{p.category}</div>
                      <h3 className="font-serif text-xl text-[#0D1B2A] mt-3 leading-snug">
                        {p.title}
                      </h3>
                      {p.excerpt && (
                        <p className="text-sm text-[#4A5568] mt-3 leading-relaxed line-clamp-3">
                          {p.excerpt}
                        </p>
                      )}
                      <div className="mt-6 flex items-center justify-between text-xs text-[#718096]">
                        <span className="inline-flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5" /> {p.read_time || 1} min read
                        </span>
                        <ArrowUpRight className="w-4 h-4 text-[#0D1B2A] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
